import LoaderDivs from "@/components/loaders/loader-divs";
import SearchInput from "@/components/search-input";
import Button from "@/components/ui/Buttons/button";
import { useGlobalContext } from "@/context/global/GlobalContext";
import { useSearchParams } from "next/navigation";
import { useEffect, useRef, useState, useMemo } from "react";
import useScrollOnDrag from "react-scroll-ondrag";
import { ROUTES, isCurrentPlayFeedPath } from "@/constants/routes";
import useEffectivePathname from "@/hooks/useEffectivePathname";
import { useTranslations } from "@/lib/static-intl";

const SEARCH_FILTERS = ["all", "graphics", "video", "user"];

const DEFAULT_PLAY_FEED_CATEGORIES = [
  { value: "카페", labelKey: "cafe" },
  { value: "맛집", labelKey: "restaurant" },
  { value: "술집", labelKey: "bar" },
  { value: "디저트", labelKey: "dessert" },
  { value: "데이트", labelKey: "date" },
  { value: "여행", labelKey: "travel" },
];

const PLAY_FEED_CATEGORY_LABEL_KEYS = DEFAULT_PLAY_FEED_CATEGORIES.reduce(
  (acc, category) => ({
    ...acc,
    [category.value]: category.labelKey,
  }),
  {}
);

const ExploreSearchTabs = ({ showInlineSearch = true }) => {
  const pathname = useEffectivePathname();
  const searchParams = useSearchParams();
  const t = useTranslations("explore.explore_search_tabs");
  const { trendingPostTerms, searchTabsLoader, updateQueryParam } =
    useGlobalContext();

  const [selectedCategory, setSelectedCategory] = useState("recommended");
  const [selectedPostFilter, setSelectedPostFilter] = useState("all");

  const [currentTrendingTerms, setCurrentTrendingTerms] = useState(
    DEFAULT_PLAY_FEED_CATEGORIES.map((category) => category.value)
  );

  const isSearched = useMemo(() => {
    return !!(searchParams.get("q") || searchParams.get("search"));
  }, [searchParams]);

  const searchQueryKey =
    pathname === ROUTES.current.search ? "q" : "search";
  const showMobileInlineSearch =
    showInlineSearch && !isCurrentPlayFeedPath(pathname);

  useEffect(() => {
    if (searchParams.get("filter") === "user") {
      setCurrentTrendingTerms([]);
    } else if (searchParams.get("q") || searchParams.get("search")) {
      setCurrentTrendingTerms([...new Set(trendingPostTerms)]);
    } else {
      setCurrentTrendingTerms(
        DEFAULT_PLAY_FEED_CATEGORIES.map((category) => category.value)
      );
    }
  }, [trendingPostTerms, searchParams]);

  useEffect(() => {
    if (
      isCurrentPlayFeedPath(pathname) &&
      searchParams.size === 0
    ) {
      setSelectedCategory("recommended");
    }
    if (selectedPostFilter) {
      updateQueryParam(
        "filter",
        selectedPostFilter === "all" ? "" : selectedPostFilter
      );
    }
  }, [pathname, searchParams, selectedPostFilter]);

  useEffect(() => {
    if (!isSearched && selectedPostFilter !== "all") {
      setSelectedPostFilter("all");
    }
  }, [isSearched, selectedPostFilter]);

  return (
    <div className="lg:mb-6">
      {showMobileInlineSearch && (
        <div className="mb-2 md:hidden">
          <SearchInput />
        </div>
      )}
      {searchTabsLoader ? (
        <div className="w-full flex gap-2 overflow-x-auto min-h-fit">
          {Array.from({ length: 15 }).map((_, i) => (
            <LoaderDivs className="h-10 min-w-28 rounded-full" key={i} />
          ))}
        </div>
      ) : (
        <>
          {isSearched && (
            <div className="w-full flex items-center justify-between mb-2">
              <div className="w-full flex items-center gap-0 md:gap-2">
                {SEARCH_FILTERS.map((filter) => (
                  <Button
                    key={filter}
                    className={`relative text-t-body capitalize !text-nowrap ${selectedPostFilter === filter
                      ? "after:absolute after:inset-x-3 after:-bottom-px after:h-[2px] after:rounded after:bg-ink dark:after:bg-ink_on_dark font-extrabold !text-ink dark:!text-ink_on_dark"
                      : ""
                      }`}
                    onClick={() => {
                      setSelectedPostFilter(filter);
                      updateQueryParam(
                        "filter",
                        filter === "all" ? "" : filter
                      );
                    }}
                  >
                    {t(filter)}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="w-full flex items-center min-h-fit gap-2 overflow-x-auto">
            <Button
              className={`relative text-t-body !text-nowrap whitespace-nowrap ${selectedCategory === "recommended"
                ? "after:absolute after:inset-x-3 after:-bottom-px after:h-[2px] after:rounded after:bg-ink dark:after:bg-ink_on_dark font-extrabold !text-ink dark:!text-ink_on_dark"
                : ""
                }`}
              onClick={() => {
                setSelectedCategory("recommended");
                updateQueryParam();
              }}
            >
              {t("recommended")}
            </Button>
            {currentTrendingTerms.map((value, index) => (
              <Button
                key={index}
                className={`relative text-t-body !text-nowrap whitespace-nowrap capitalize ${selectedCategory === value
                  ? "after:absolute after:inset-x-3 after:-bottom-px after:h-[2px] after:rounded after:bg-ink dark:after:bg-ink_on_dark font-extrabold !text-ink dark:!text-ink_on_dark"
                  : ""
                  }`}
                onClick={() => {
                  setSelectedCategory(value);
                  updateQueryParam(searchQueryKey, value);
                }}
              >
                {PLAY_FEED_CATEGORY_LABEL_KEYS[value]
                  ? t(PLAY_FEED_CATEGORY_LABEL_KEYS[value])
                  : value}
              </Button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ExploreSearchTabs;
